import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import Box from '@material-ui/core/Box';
import {useAnalyticsFilters} from 'components/AnalyticsFilters/AnalyticsFiltersContext';
import {
  hasAnalyticsDateRange,
  resolveAnalyticsRequestDates
} from 'helpers/services/api/analyticsHelpers';
import SalesCountChart from 'components/feature/Tabs/SalesCountChart';
import {useDatePickerContext} from '../../Datepicker/DatePickerContext';
import Loading from '../../shared/Utils/Loading';
import {
  DashboardBox,
  DashboardBoxes,
  DashboardBoxNumber,
  DashboardBoxText,
  NoResultText
} from '../../../routes/App/dashboard/styles';
import {GET_SALES_COUNT} from '../../../store/analyticsSalesCount/action';
import roundTheNumber from '../../../helpers/functionUtils/roundTheNumber';

const TotalSales = ({disableFetch, dataOverride, loadingOverride}) => {
  const dispatch = useDispatch();
  const {locationId} = useSelector((state) => state.user);
  const {salesCount, salesCountLoading} = useSelector(
    (state) => state.analyticsSalesCountReducer
  );

  const date = useDatePickerContext();
  const filters = useAnalyticsFilters();

  useEffect(() => {
    if (disableFetch || !locationId || !hasAnalyticsDateRange(date)) {
      return;
    }
    const {startDate, endDate} = resolveAnalyticsRequestDates(date);
    dispatch(
      GET_SALES_COUNT({
        startDate,
        endDate,
        channelTypes: filters.channelTypes,
        fulfillmentModes: filters.fulfillmentModes
      })
    );
  }, [
    date,
    locationId,
    dispatch,
    disableFetch,
    filters.channelTypes,
    filters.fulfillmentModes
  ]);

  const resolvedSalesCount = dataOverride ?? salesCount;
  const resolvedLoading = loadingOverride ?? salesCountLoading;

  function nFormatter(num) {
    if (num >= 1000000000) {
      return `${(num / 1000000000).toFixed(2).replace(/\.0$/, '')}G`;
    }
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2).replace(/\.0$/, '')}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(2).replace(/\.0$/, '')}K`;
    }
    return roundTheNumber(num);
  }

  return (
    <>
      {resolvedLoading && <Loading />}
      {Object.keys(resolvedSalesCount).length !== 0 && !resolvedLoading && (
        <>
          <DashboardBoxes>
            <DashboardBox>
              <DashboardBoxNumber>
                ${nFormatter(resolvedSalesCount.totalSum)}
              </DashboardBoxNumber>
            </DashboardBox>
            <DashboardBox>
              <DashboardBoxText>
                <strong>{resolvedSalesCount.orders}</strong> Orders (total)
              </DashboardBoxText>
              <DashboardBoxText>
                <strong>
                  ${roundTheNumber(resolvedSalesCount.avgOrderSize)}
                </strong>{' '}
                Order Value (avg.)
              </DashboardBoxText>
            </DashboardBox>
          </DashboardBoxes>
          <SalesCountChart series={resolvedSalesCount.series} />
        </>
      )}

      {!resolvedLoading && Object.keys(resolvedSalesCount).length === 0 && (
        <Box display='flex' alignItems='center' justifyContent='center' m={1}>
          <NoResultText
            variant='body1'
            component='h6'
            align='right'
            className='noResults'
          >
            No results found for this date range!
          </NoResultText>
        </Box>
      )}
    </>
  );
};

TotalSales.defaultProps = {
  disableFetch: false,
  dataOverride: null,
  loadingOverride: undefined
};

TotalSales.propTypes = {
  disableFetch: PropTypes.bool,
  dataOverride: PropTypes.object,
  loadingOverride: PropTypes.bool
};

export default TotalSales;
